import { Tabs, Tooltip } from "antd";
import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  getLeadAvailableRootOppTypeIds,
  getRootOppTypeId,
  getUserAllowedRootOppTypeIds,
  isUserUnscoped,
} from "../../../library/oppTypeScoping";
import {
  DEFAULT_ROOT_OPP_TYPES,
  getRootConfig,
  ROOT_OPP_TYPES,
} from "../../../library/oppTypeRootConfig";
import { getLocationId } from "../../../redux/slices/locationSlice";
import "./OppTypeRootTabs.css";

// Tabs are driven by the LOCATION CURRENTLY ACTIVE in the top-bar
// dropdown (``state.location.locationId``), NOT the lead's home
// location — switching rooftops in the header re-scopes the
// activity-center tab strip too. Eligible roots come from
// ``Location.activeRootOppTypeIds`` (a distinct of
// ``Department.opportunity_type_id`` at that location, BE-supplied).
//
// "All locations" mode (no specific location picked) shows the union
// of root opp_types across every location the user can access, so
// the user sees every tab they could potentially act on.
//
// Fallback: when the resolved set is empty (a location with no
// Department rows, or every Department row missing an
// ``opportunity_type_id`` mapping), use ``DEFAULT_ROOT_OPP_TYPES``
// — Sales / Service / Parts only. Tenant-specific roots like
// AutoSavvy CS never surface via the fallback; the customer must
// have a real Department mapping for them to appear.
//
// Both the ink-bar AND the active label color use the active root's
// ``activeColor`` via the ``--opp-tabs-ink-color`` CSS variable set
// inline on the Tabs wrapper.
const DEFAULT_INK_COLOR = "#1677ff";

const OppTypeRootTabs = ({
  lead,
  user,
  value,
  onChange,
  unreadByRoot = {},
  // "All opportunity" is selected in the message thread — no single
  // root applies, so don't let the tab strip fall back to highlighting
  // the first enabled tab (would misleadingly imply "Sales" is active).
  noActiveTab = false,
}) => {
  // Top-bar dropdown's currently-selected location. ``null`` ⇒ "All
  // locations" — we union across every location the user can access.
  // Used by BOTH the ``rootNameById`` map AND the ``eligibleRootIds``
  // derivation below.
  const topBarLocationId = useSelector(getLocationId);

  // Map a root opp_type id → display name. Resolution order:
  //   1. BE-supplied ``activeRootOppTypes`` from the user's accessible
  //      locations (preferred — works even when the lead has no opp
  //      under that root, which is exactly when we need the label).
  //   2. BE-supplied ``activeRootOppTypes`` from the lead's home
  //      location (covers the edge case where the lead is at a
  //      location not in the user's accessible set).
  //   3. The lead's own opp graph — walk each opp's opportunityType
  //      chain to the root and pluck its ``name``.
  // The fallback config in ``getRootConfig`` uses this when an entry
  // for the root id isn't in ``ROOT_OPP_TYPES``.
  const rootNameById = useMemo(() => {
    const map = new Map();
    const consume = list => {
      if (!Array.isArray(list)) return;
      list.forEach(item => {
        if (!item || item.id == null || !item.name) return;
        map.set(Number(item.id), item.name);
      });
    };

    const userLocations = Array.isArray(user?.locations) ? user.locations : [];
    // (1) When a specific top-bar location is picked, prefer its
    // names — when null, union across all accessible locations.
    if (topBarLocationId != null) {
      const match = userLocations.find(ul => Number(ul?.location?.id) === Number(topBarLocationId));
      consume(match?.location?.activeRootOppTypes);
    } else {
      userLocations.forEach(ul => consume(ul?.location?.activeRootOppTypes));
    }
    // (2) Lead-home-location fallback.
    consume(lead?.location?.activeRootOppTypes);
    // (3) Lead opp graph fallback (only fills slots not yet covered).
    (lead?.leadOpportunities || []).forEach(opp => {
      const ot = opp?.opportunityType;
      if (!ot) return;
      const rootId = getRootOppTypeId(ot);
      if (!rootId) return;
      let cursor = ot;
      for (let i = 0; i < 5; i += 1) {
        if (!cursor) break;
        if (Number(cursor.id) === Number(rootId)) {
          if (cursor.name && !map.has(Number(rootId))) {
            map.set(Number(rootId), cursor.name);
          }
          break;
        }
        cursor = cursor.parentType;
      }
    });
    return map;
  }, [
    topBarLocationId,
    user?.locations,
    lead?.id,
    lead?.location?.activeRootOppTypes,
    lead?.leadOpportunities,
  ]);

  // The set of root opp_type ids that should be eligible for tabs.
  // Resolution order:
  //   1. Specific location picked in the top bar → that location's
  //      ``activeRootOppTypeIds`` from ``user.locations``.
  //   2. ``null`` (All locations) → union across every entry in
  //      ``user.locations``.
  //   3. Empty result → fall back to ``lead.location.activeRootOppTypeIds``
  //      (still useful when the user landed on a lead at a location
  //      that isn't in ``user.locations`` yet).
  //   4. Still empty → ``DEFAULT_ROOT_OPP_TYPES`` (Sales / Service /
  //      Parts) so locations without any Department mapping keep
  //      working. Tenant-specific roots (e.g. AutoSavvy CS) are
  //      INTENTIONALLY excluded from this fallback — they require a
  //      real Department mapping at the BE.
  const eligibleRootIds = useMemo(() => {
    const userLocations = Array.isArray(user?.locations) ? user.locations : [];
    const collect = idsFromOneLocation => {
      if (!Array.isArray(idsFromOneLocation)) return [];
      return idsFromOneLocation.map(Number).filter(n => !Number.isNaN(n));
    };

    // Specific top-bar location selected → just that one's roots.
    if (topBarLocationId != null) {
      const match = userLocations.find(ul => Number(ul?.location?.id) === Number(topBarLocationId));
      const ids = collect(match?.location?.activeRootOppTypeIds);
      if (ids.length > 0) return ids;
    } else if (userLocations.length > 0) {
      // All-locations mode → union across every accessible location.
      const union = new Set();
      userLocations.forEach(ul =>
        collect(ul?.location?.activeRootOppTypeIds).forEach(id => union.add(id))
      );
      if (union.size > 0) return [...union];
    }

    // Lead-home-location fallback — only matters when the lead is at a
    // location not in ``user.locations`` (rare, but defensive).
    const fromLead = collect(lead?.location?.activeRootOppTypeIds);
    if (fromLead.length > 0) return fromLead;

    // Legacy / no-Department-setup → industry-default roots only
    // (Sales / Service / Parts). Tenant-specific roots like
    // AutoSavvy CS must be backed by a real Department mapping to
    // surface — they never appear via the default fallback.
    return DEFAULT_ROOT_OPP_TYPES.map(c => c.rootOppTypeId);
  }, [topBarLocationId, user?.locations, lead?.location?.activeRootOppTypeIds]);

  // Tabs are disabled when either:
  //   (a) the lead has no opportunity under that root, OR
  //   (b) the user isn't permitted for that root.
  // Unscoped users (admins / no UserDepartment) bypass (b).
  const disabledMap = useMemo(() => {
    const leadRoots = getLeadAvailableRootOppTypeIds(lead);
    const userIsUnscoped = isUserUnscoped(user);
    const userAllowed = getUserAllowedRootOppTypeIds(user);
    return eligibleRootIds.reduce((acc, rootId) => {
      const noOpp = !leadRoots.has(rootId);
      const noPermission = !userIsUnscoped && !userAllowed.has(rootId);
      acc[rootId] = noOpp || noPermission;
      return acc;
    }, {});
  }, [lead, user, eligibleRootIds]);

  // Look up the active tab's color so the ink-bar + label both render
  // in the matching root color. Fallback to AntD blue when no tab is
  // active (initial render before default-tab resolves).
  const activeColor = useMemo(() => {
    if (value == null) return DEFAULT_INK_COLOR;
    const cfg = getRootConfig(value, rootNameById.get(Number(value)));
    return cfg.activeColor || DEFAULT_INK_COLOR;
  }, [value, rootNameById]);

  // Build items in AntD <Tabs> v5 shape. One per eligible root id;
  // visuals come from ``getRootConfig`` which gracefully falls back
  // for roots not in ROOT_OPP_TYPES.
  const items = useMemo(
    () =>
      eligibleRootIds.map(rootId => {
        const cfg = getRootConfig(rootId, rootNameById.get(Number(rootId)));
        const { Icon } = cfg;
        const isActive = Number(value) === Number(rootId);
        const isDisabled = !!disabledMap[rootId];
        // Pre-color the icon: active uses the tab's own color, disabled
        // greys out, inactive uses AntD's neutral icon color.
        let iconColor = "rgba(0, 0, 0, 0.45)";
        if (isDisabled) iconColor = "rgba(0, 0, 0, 0.25)";
        else if (isActive) iconColor = cfg.activeColor;
        // Per-tab unread indicator: socket messages that arrived for
        // this root while the user was on a different tab.
        const unreadCount = unreadByRoot?.[rootId] || 0;
        const showUnreadDot = unreadCount > 0 && !isActive && !isDisabled;
        // Compact label for the tab strip — falls back to displayName
        // when shortLabel equals displayName (e.g. Sales/Service). For
        // long names like "AutoSavvy Customer Service" → "ACS" and we
        // surface the full text via a tooltip.
        const visibleLabel = cfg.shortLabel || cfg.displayName;
        const showHoverFull =
          cfg.displayName && cfg.shortLabel && cfg.shortLabel !== cfg.displayName;
        const labelNode = (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <Icon size={16} color={iconColor} strokeWidth={1.66} />
            {visibleLabel}
            {showUnreadDot && (
              <span
                aria-label={`${unreadCount} unread message${unreadCount === 1 ? "" : "s"} in ${
                  cfg.displayName
                }`}
                title={`${unreadCount} new message${unreadCount === 1 ? "" : "s"} in ${
                  cfg.displayName
                }`}
                style={{
                  display: "inline-block",
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: "#ff4d4f",
                  flexShrink: 0,
                }}
              />
            )}
          </span>
        );
        return {
          key: String(rootId),
          disabled: isDisabled,
          label: showHoverFull ? (
            <Tooltip title={cfg.displayName} placement="top">
              {labelNode}
            </Tooltip>
          ) : (
            labelNode
          ),
        };
      }),
    [eligibleRootIds, value, disabledMap, unreadByRoot, rootNameById]
  );

  // When the parent hasn't picked an active root yet (search-by-phone
  // flow, initial render before the default-tab effect resolves),
  // AntD's Tabs falls back to the first item in ``items`` — which
  // would be Sales even for a Service-only lead. Pre-compute a
  // fallback that lands on the first ENABLED tab instead, so the
  // initial render matches the lead's actual opps.
  const fallbackActiveKey = useMemo(() => {
    const firstEnabled = eligibleRootIds.find(id => !disabledMap[id]);
    return firstEnabled != null ? String(firstEnabled) : undefined;
  }, [eligibleRootIds, disabledMap]);

  return (
    <Tabs
      className="opp-type-tabs"
      // CSS variable consumed by OppTypeRootTabs.css to paint both the
      // ink-bar and the active label in the active tab's color.
      style={{ "--opp-tabs-ink-color": activeColor }}
      activeKey={noActiveTab ? "__none__" : value != null ? String(value) : fallbackActiveKey}
      onChange={key => {
        // Defensive: callers that mount LeadProfile without threading
        // a setter for the active root opp_type (legacy paths, future
        // surfaces) get a clean no-op instead of "l is not a function".
        if (typeof onChange === "function") onChange(Number(key));
      }}
      items={items}
    />
  );
};

export default OppTypeRootTabs;
